/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import { Link } from 'react-router-dom';
import PublicFooter from '../components/PublicFooter';

const DEMO_MAILTO =
  'mailto:info@skilldelivery.co.in?subject=SkillDelivery%20demo%20request';

const STEPS = [
  {
    number: '01',
    icon: '🚀',
    title: 'Create Your Account',
    subtitle: 'The first step in making YOU visible.',
    intro: 'Sign up and choose how you will use SkillDelivery.',
    bodyHeading: 'You will be asked for:',
    bulletList: [
      'Full name and working email',
      'Password (8+ characters)',
      'Entity type: Individual, Organization, University, or Institute',
      'Primary domain: WMS, iSeries, Civil, Apparel, or ERPNext'
    ],
    callout: 'Note: Entity type cannot be changed after registration. Choose carefully.',
    closing:
      '2FA is optional for Individuals, mandatory for Organizations, Universities and Institutes.'
  },
  {
    number: '02',
    icon: '👤',
    title: 'Complete Your Profile',
    subtitle: "What you know, what you've built — captured.",
    intro: 'After login, Dashboard → Edit Profile.',
    bodyHeading: 'What to add (and why it matters):',
    bulletList: [
      'Role / Designation — SkillBot adjusts answer depth',
      'Years of Experience — calibrates complexity of responses',
      'Organization Name — provides operational context',
      'LinkedIn / Website — strengthens your talent visibility'
    ],
    closing:
      'A complete profile means SkillBot gives you more relevant, contextual answers.'
  },
  {
    number: '03',
    icon: '🎯',
    title: 'Set Your Goals',
    subtitle: "What you're working toward becomes part of your footprint.",
    intro: 'Go to Workspace → Goals. Add 2–3 goals that matter to you.',
    bodyHeading: 'Examples per domain:',
    iconList: [
      { icon: '📦', text: 'WMS: "Find a WMS implementation job in India" or "Get WMS consultant certification"' },
      { icon: '💻', text: 'iSeries: "Understand RPGLE modernization" or "Migrate legacy AS400 programs"' },
      { icon: '🏗️', text: 'Civil: "Master EVM for government projects" or "Understand CPWD contract management"' },
      { icon: '✂️', text: 'Apparel: "Understand fabric relaxation processes" or "Reduce quality failures"' }
    ],
    closing:
      'Goals make Wiki Intelligence and Scout suggestions directly relevant to what you are trying to achieve.'
  },
  {
    number: '04',
    icon: '🤖',
    title: 'Talk to SkillBot',
    subtitle: 'Every question becomes part of your knowledge contribution.',
    intro:
      'Go to SkillBot in the left nav. Ask domain questions in plain English — like talking to a 20-year veteran in your field.',
    bodyHeading: 'Sample questions to try:',
    iconList: [
      { icon: '📦', text: '"What is wave picking in WMS?" / "How does putaway logic work in Manhattan?"' },
      { icon: '💻', text: '"What is STRSRVJOB used for?" / "How do I modernize RPG programs?"' },
      { icon: '🏗️', text: '"What is EVM in construction projects?" / "How does CPWD billing work?"' },
      { icon: '✂️', text: '"What is fabric relaxation?" / "Common quality failures in stitching?"' }
    ],
    callout:
      'First ask on a new topic may take 30-90 seconds (model loading). The same question next time is instant — this is SEAL working as designed.',
    closing:
      'SkillBot answers from Wiki Intelligence first (instant, free) and falls back to fine-tuned adapter only for new questions.'
  },
  {
    number: '05',
    icon: '📚',
    title: 'Explore Learning Hub',
    subtitle: 'Discover what others in your domain are building.',
    intro: 'Go to Learning Hub in the left nav.',
    bodyHeading: 'Find curated domain resources — all human-verified, all real URLs:',
    iconList: [
      { icon: '📺', text: 'YouTube channels — practitioner-recommended for your domain' },
      { icon: '🎓', text: 'Certifications — Coursera, ASCM, GAQM, Alison with direct links' },
      { icon: '💼', text: 'Jobs — relevant job boards and company career pages' },
      { icon: '👥', text: 'Communities — LinkedIn groups, professional associations, forums' },
      { icon: '📰', text: 'Articles — industry reports, trend analyses, best practice guides' }
    ],
    closing: 'No SEO-fluff content. Every link is verified by a real practitioner.'
  },
  {
    number: '06',
    icon: '🗺️',
    title: 'Build Your Roadmap',
    subtitle: 'Your path forward — visible to you, your peers, your mentors.',
    intro:
      'Go to Roadmap. The platform generates a personalized learning path based on your domain, goals, skill level, and entity type.',
    closing:
      'Tip: Set your Goals in Step 3 BEFORE visiting Roadmap — your goals directly shape the path generated for you.'
  },
  {
    number: '07',
    icon: '🤝',
    title: 'Connect via xConnect',
    subtitle: 'Where your expertise meets the people who should know about it.',
    intro:
      'Go to xConnect or Community. AI matches you with professionals, organizations, universities, and institutes based on domain context and goals — not just location or keywords.',
    bodyHeading: 'What xConnect finds for you:',
    iconList: [
      { icon: '👤', text: 'As Individual: organizations hiring for your domain, mentors, peers' },
      { icon: '🏢', text: 'As Organization: verified domain talent, universities with graduates in your vertical' },
      { icon: '🎓', text: "As University: organizations needing graduates with your students' skills" },
      { icon: '🏫', text: 'As Institute: organizations needing corporate training in your delivery domains' }
    ],
    closing: 'Purposeful discovery. Not random networking.'
  }
];

const scrollToStep = (e, number) => {
  e.preventDefault();
  const el = document.getElementById(`step-${number}`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const TourPage = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">

      {/* ─── Decorative radial glows ─────────────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32"
        style={{
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24"
        style={{
          width: '400px',
          height: '400px',
          background:
            'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
        }}
      />

      {/* ─── Sticky progress indicator ───────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-brand-border">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/welcome" className="flex items-center gap-2 shrink-0">
            <img src="/logo.jpg" alt="SkillDelivery" className="h-7 w-auto" />
            <span className="font-bold text-base tracking-tight text-brand-text">
              Skill<span className="text-brand-cyan">Delivery</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1" aria-label="Tour progress">
            {STEPS.map((step, i) => (
              <React.Fragment key={step.number}>
                <a
                  href={`#step-${step.number}`}
                  onClick={(e) => scrollToStep(e, step.number)}
                  className="w-8 h-8 rounded-full bg-brand-bgOff2 text-brand-muted2 text-xs font-bold flex items-center justify-center hover:bg-brand-cyan hover:text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
                  aria-label={`Jump to step ${parseInt(step.number, 10)}`}
                >
                  {parseInt(step.number, 10)}
                </a>
                {i < STEPS.length - 1 && (
                  <div className="w-6 h-px bg-brand-border" aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </div>

          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark font-medium shrink-0"
          >
            ← Back
          </Link>
        </div>
      </nav>

      {/* ─── Hero ────────────────────────────────────────────────── */}
      <header className="relative max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
        <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
          7-step getting started
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-text">
          How to use SkillDelivery
        </h1>
        <p className="mt-4 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
          From sign-up to your first connection. Seven steps.
        </p>
      </header>

      {/* ─── Step cards ──────────────────────────────────────────── */}
      <section
        aria-label="Tour steps"
        className="relative max-w-4xl mx-auto px-6 space-y-6 pb-12"
      >
        {STEPS.map((step) => (
          <article
            key={step.number}
            id={`step-${step.number}`}
            className="scroll-mt-24 bg-white border border-brand-border rounded-brandCard p-8 sm:p-10 hover:shadow-brand transition-all duration-200"
          >
            <div className="flex items-start gap-4 sm:gap-6">

              {/* Number badge */}
              <div
                className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-brandCard bg-gradient-to-br from-brand-cyanLight to-brand-blue shadow-brand flex items-center justify-center"
                aria-hidden="true"
              >
                <span className="text-2xl sm:text-3xl font-extrabold text-white">
                  {step.number}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan mb-2">
                  Step {parseInt(step.number, 10)} of {STEPS.length}
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
                  <span className="mr-2" aria-hidden="true">{step.icon}</span>
                  {step.title}
                </h2>

                {step.subtitle && (
                  <p className="mt-3 text-base sm:text-lg italic text-brand-cyan font-medium leading-relaxed">
                    {step.subtitle}
                  </p>
                )}

                {step.intro && (
                  <p className="mt-4 text-base sm:text-lg text-brand-text leading-relaxed">
                    {step.intro}
                  </p>
                )}

                {step.body && (
                  <div className="mt-4 space-y-2">
                    {step.body.map((p, i) => (
                      <p key={i} className="text-base text-brand-muted leading-relaxed">
                        {p}
                      </p>
                    ))}
                  </div>
                )}

                {step.bodyHeading && (
                  <p className="mt-4 text-base font-semibold text-brand-text">
                    {step.bodyHeading}
                  </p>
                )}

                {step.bulletList && (
                  <ul className="mt-3 space-y-2">
                    {step.bulletList.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span
                          className="text-brand-cyan font-bold mt-0.5"
                          aria-hidden="true"
                        >
                          ✓
                        </span>
                        <span className="text-base text-brand-muted leading-relaxed">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {step.callout && (
                  <div className="mt-4 px-4 py-3 rounded-brand border-l-4 border-brand-cyan bg-brand-bgOff2">
                    <p className="text-sm italic text-brand-muted2 leading-relaxed">
                      {step.callout}
                    </p>
                  </div>
                )}

                {step.iconList && (
                  <ul className="mt-4 space-y-3">
                    {step.iconList.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-xl leading-none mt-0.5" aria-hidden="true">
                          {item.icon}
                        </span>
                        <span className="text-base text-brand-muted leading-relaxed">
                          {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {step.closing && (
                  <p className="mt-5 text-base text-brand-text leading-relaxed">
                    {step.closing}
                  </p>
                )}

                {step.quote && (
                  <p className="mt-4 text-base italic text-brand-muted leading-relaxed">
                    {step.quote}
                  </p>
                )}
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* ─── Final CTA ───────────────────────────────────────────── */}
      <section className="relative max-w-3xl mx-auto px-6 pt-8 pb-20 text-center">
        <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
          Ready to get started?
        </p>
        <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-text tracking-tight">
          Pick the path that fits where you are.
        </h2>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
          <Link
            to="/register"
            className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
          >
            Sign up free →
          </Link>
          <a
            href={DEMO_MAILTO}
            className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
          >
            Book a demo with Lalit
          </a>
          <Link
            to="/welcome"
            className="px-6 py-3 text-brand-muted hover:text-brand-text font-medium hover:underline transition-colors"
          >
            Keep browsing
          </Link>
        </div>

        {/* ── Manifesto closing ── */}
        <p className="mt-10 text-base text-brand-text2">
          Welcome home.{' '}
          <Link
            to="/manifesto"
            className="text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            Read our manifesto →
          </Link>
        </p>
      </section>

      <PublicFooter />
    </div>
  );
};

export default TourPage;
